import {
  Point,
  RingData,
  SegmentShapeType,
} from 'ui/quality-control/board/types';

import { mirrorNormalizedPoint } from './mirror-normalized-point';

type SegmentGeometry = {
  width: number;
  tilt: { value: number; extendedValue: number };
};

const MIRRORED_SHAPE_TYPE: Record<SegmentShapeType, SegmentShapeType> = {
  [SegmentShapeType.Key]: SegmentShapeType.KeyInverted,
  [SegmentShapeType.KeyInverted]: SegmentShapeType.Key,
  [SegmentShapeType.HalfKey]: SegmentShapeType.HalfKey,
  [SegmentShapeType.TiltedLeft]: SegmentShapeType.TiltedLeft,
  [SegmentShapeType.TiltedLeftExtended]: SegmentShapeType.TiltedLeftExtended,
  [SegmentShapeType.TiltedRight]: SegmentShapeType.TiltedRight,
  [SegmentShapeType.TiltedRightExtended]: SegmentShapeType.TiltedRightExtended,
};

// a ring's segments are laid out on a cycle of width `ringSegmentsWidth`; reversing their
// order (below) mirrors that cycle, so the ring's own offset on it (marginX) must mirror
// too, otherwise the ring ends up shifted instead of lining up under its mirrored hour mark.
const mirrorMarginX = (marginX: number, ringSegmentsWidth: number): number => {
  if (ringSegmentsWidth <= 0) {
    return marginX;
  }
  return (
    (((ringSegmentsWidth - (marginX % ringSegmentsWidth)) % ringSegmentsWidth) +
      ringSegmentsWidth) %
    ringSegmentsWidth
  );
};

// a segment's polygon isn't a plain rectangle: its right edge is offset from its left edge
// by this amount (see getPointsByShapeType in ui/quality-control/board/segment.tsx), so its
// horizontal center of symmetry sits at (width + tiltOffset) / 2, not width / 2. Mirroring a
// stroke's x without this offset shifts it left by tiltOffset/width — this replicates the
// shape formulas' offsets exactly, including the extended tilt shapes' asymmetric mix of
// tiltValue/extendedTiltValue, so the mirror lines up with the actual rendered polygon.
const getShapeTiltOffset = (
  shapeType: SegmentShapeType,
  tiltValue: number,
  extendedTiltValue: number,
): number => {
  switch (shapeType) {
    case SegmentShapeType.HalfKey:
    case SegmentShapeType.TiltedLeftExtended:
      return extendedTiltValue;
    case SegmentShapeType.Key:
    case SegmentShapeType.KeyInverted:
    case SegmentShapeType.TiltedLeft:
    case SegmentShapeType.TiltedRight:
    case SegmentShapeType.TiltedRightExtended:
      return tiltValue;
    default:
      return 0;
  }
};

const mirrorStrokePoint = (
  point: Point,
  shapeType: SegmentShapeType,
  segment: SegmentGeometry,
): Point => {
  const tiltOffset = getShapeTiltOffset(
    shapeType,
    segment.tilt.value,
    segment.tilt.extendedValue,
  );
  const tiltFraction = segment.width > 0 ? tiltOffset / segment.width : 0;

  return mirrorNormalizedPoint(point, tiltFraction);
};

// entry mode shows the highest ringNumber first with segments in tunnel-installation order;
// exit mode looks at the ring from the other end of the tunnel, so ring order and segment
// order both reverse, each ring's marginX mirrors to stay aligned on the hour axis, the
// segment key's shape mirrors horizontally, and defect strokes rotate 180deg within their
// segment's box (accounting for the segment's own tilt, see getShapeTiltOffset above).
export const getExitViewRings = (
  rings: RingData[],
  segment: SegmentGeometry,
): RingData[] =>
  [...rings].reverse().map((ring) => ({
    ...ring,
    marginX: mirrorMarginX(ring.marginX, ring.segments.length * segment.width),
    segments: [...ring.segments].reverse().map((originalSegment) => ({
      ...originalSegment,
      shapeType: MIRRORED_SHAPE_TYPE[originalSegment.shapeType],
      defects: originalSegment.defects.map((defect) => ({
        ...defect,
        strokes: defect.strokes.map((point) =>
          mirrorStrokePoint(point, originalSegment.shapeType, segment),
        ),
      })),
    })),
  }));
